import * as Mui from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { CheckboxTable } from "../components/checkbox/checkbox-table";
import StyledSwitch from "../components/form/styled-switch";
import { PriceInputField } from "../components/product/product-price-layout";
import { IconChevronDown, IconChevronRight, IconTrash } from "../components/svg";
import { formatCurrency } from "./util";
import { useState, useCallback, useMemo } from "react";
import React from "react";
import { WarningPopover } from "../components/form/warning-popover";

const getCategoryIndex = (formikProps, categoryId) => {
    return formikProps?.values?.productsData?.findIndex(cat => cat.category_id === categoryId);
}

export const formatExclusionCategories = (priceRuleDetails, productCategories) => {

    const exclusionCategories = []
    const categoryIds = priceRuleDetails?.priceRuleExclusionData?.categoryIds
    const productIds = priceRuleDetails?.priceRuleExclusionData?.productIds

    const priceRuleCategories = categoryIds?.map(catId => productCategories?.flatMap(cat => cat?.children).find(child => child?.id === catId))

    priceRuleCategories?.forEach(cat => {
        const catProducts = priceRuleDetails?.canteenProduct?.filter(prd => prd?.product?.productCategoryId?.toString() === cat?.id?.toString())
        exclusionCategories?.push({
            ...cat,
            isAllProductsSelected: true,
            products: catProducts?.map(prd => ({ id: prd?.product?.id, name: prd?.product?.name }))
        })
    })

    // Go through all the productIds and create a set of productCategoryId
    if (Array.isArray(productIds) && productIds.length > 0) {
        // Flatten all canteen products so we can find by id
        const allCanteenProducts = priceRuleDetails?.canteenProduct ?? [];
        // Find all products that match any of the productIds
        const excludedProducts = allCanteenProducts.filter(prd => productIds.includes(prd?.product?.id));
        // Map productCategoryIds from these products and deduplicate
        const categoryIdSet = new Set(excludedProducts.map(prd => prd?.product?.productCategoryId));

        categoryIdSet.forEach(catId => {
            // Find category info
            const category = productCategories?.flatMap(cat => cat?.children).find(child => child?.id === catId)
            if (!category) return;
            // All products in this category in the productIds set
            const catProducts = excludedProducts
                .filter(prd => prd?.product?.productCategoryId === catId)
                .map(prd => ({ id: prd?.product?.id, name: prd?.product?.name }));
            exclusionCategories.push({
                ...category,
                isAllProductsSelected: false,
                products: catProducts
            });
        });
    }
    return exclusionCategories
}

export const createInitialValues = (priceRuleDetails, productCategories, id, priceRuleId) => {

    if (priceRuleId) {

        const exclusionCategories = formatExclusionCategories(priceRuleDetails, productCategories)

        return {
            name: priceRuleDetails?.name,
            startDate: priceRuleDetails?.startDate,
            endDate: priceRuleDetails?.endDate ?? '',
            ruleType: priceRuleDetails?.ruleType,
            canteenId: id,
            deductionEnabled: priceRuleDetails?.deductionEnabled,
            deductionType: priceRuleDetails?.deductionType,
            deductionValue: +priceRuleDetails?.deductionValue,
            deductionMaxLimit: +priceRuleDetails?.deductionMaxLimit,
            status: priceRuleDetails?.status,
            productsData: priceRuleDetails?.categoryPriceRuleValues?.map((item, catIndex) => {
                const category = productCategories?.flatMap(cat => cat?.children).find(child => child?.id === item?.categoryId)
                const catProducts = priceRuleDetails?.canteenProduct?.filter(prd => prd?.product?.productCategoryId?.toString() === item?.categoryId?.toString())
                return {
                    category_id: item.categoryId,
                    deductionValue: +item?.valuePercentage,
                    status: item?.status ?? 1,
                    category,
                    index: catIndex,
                    products: catProducts.map((prd, prdIndex) => {
                        const productSubsidyPrice = prd?.finalPrices?.reduce((prevObj, curr) => {
                            const valuePercentage = prd?.priceRules?.length > 0 ? prd?.priceRules?.find(rule => rule?.vatRate === curr.vatRate)?.valuePercentage : +item?.valuePercentage
                            const subsidyPrice = (+(curr.priceIncludingVat) * (+(valuePercentage) / 100)).toFixed(2)
                            return (
                                {
                                    ...prevObj,
                                    [`product_subsidy_price_vc_${curr.vatCode}`]: subsidyPrice
                                }
                            )
                        }, {})
                        const priceStatusActive = prd?.priceRules?.reduce((prevObj, curr) => {
                            return (
                                {
                                    ...prevObj,
                                    [`status_subsidy_vc_${curr.vatCode}`]: curr?.status
                                })
                        }, {})
                        return ({
                            product_id: prd?.product?.id,
                            product_uuid: prd?.product?.uuid,
                            subsidy_percentage: prd?.priceRules?.length > 0 ? +prd?.priceRules?.[0]?.valuePercentage : +item?.valuePercentage,
                            status: prd?.priceRules?.length > 0 ? +prd?.priceRules?.[0]?.status : +item?.status,
                            index: prdIndex,
                            ...productSubsidyPrice,
                            ...priceStatusActive,
                            extraPrd: prd
                        })
                    })
                }
            }),
            exclusionCategories
        }
    }

    return {
        name: '',
        startDate: '',
        endDate: '',
        ruleType: 'subsidy',
        canteenId: id,
        deductionEnabled: 0,
        deductionType: 'amount',
        deductionValue: '',
        deductionMaxLimit: '',
        status: 1,
        productsData: [],
        exclusionCategories: []

    }

}

const getCategoryProductIndex = (formikProps, categoryIndex, productId) => {
    return formikProps?.values?.productsData?.[categoryIndex]?.products?.findIndex(prd => prd.product_id === productId);
}

export const highlightSearchTerm = (searchTerm, text, maxChars) => {
    const str = text == null ? '' : String(text);
    if (!searchTerm) {
        if (maxChars) {
            if (str.length <= maxChars) return str;
            return str.slice(0, maxChars) + '...';
        }
        return str;
    }

    // if (!maxChars) {
    return str.split(new RegExp(`(${searchTerm})`, 'i')).map((part, i) =>
        part.toLowerCase() === searchTerm.toLowerCase() ?
            <span key={i} style={{ backgroundColor: '#ffff00' }}>{part}</span> :
            part
    )
    // }


    // Handle edge cases
    // if (!text) return '';
    // if (!maxChars) maxChars = 9999;

    // // Normalize for case-insensitive search
    // const lowerText = text.toLowerCase();
    // const lowerSearch = searchTerm.toLowerCase();
    // const matchIndex = lowerText.indexOf(lowerSearch);

    // // If no match, truncate text if needed
    // if (matchIndex === -1) {
    //     if (text.length <= maxChars) return text;
    //     return text.slice(0, maxChars) + '...';
    // }

    // const matchEnd = matchIndex + searchTerm.length;
    // let remaining = maxChars - searchTerm.length;

    // // Show a preview of the starting text for context (help "better understand the product")
    // const extraPrefixLen = Math.min(8, matchIndex); // Show up to 8 chars from the start if applicable

    // // If match is very early, just show the actual start, else show ellipsis
    // const startContextIndex = (matchIndex - extraPrefixLen > 0) ? matchIndex - extraPrefixLen : 0;
    // const includePrefix = startContextIndex > 0;
    // const contextBefore = text.substring(startContextIndex, matchIndex);
    // const contextAfterStart = startContextIndex;

    // // For the "before/after" logic
    // let beforeLen = Math.floor(remaining / 2);
    // let afterLen = remaining - beforeLen;

    // // Adjust for string bounds
    // if (matchIndex < beforeLen) {
    //     beforeLen = matchIndex;
    //     afterLen = remaining - beforeLen;
    // }
    // if ((text.length - matchEnd) < afterLen) {
    //     afterLen = text.length - matchEnd;
    //     beforeLen = remaining - afterLen;
    //     if (beforeLen > matchIndex) beforeLen = matchIndex;
    // }

    // let before = text.substring(Math.max(0, matchIndex - beforeLen), matchIndex);
    // let after = text.substring(matchEnd, matchEnd + afterLen);

    // // Always show some first work for understanding (the preview prefix)
    // let firstPrefix = '';
    // if (startContextIndex > 0) {
    //     firstPrefix = text.substring(0, Math.max(0, Math.min(startContextIndex, 8)));
    // } else if (startContextIndex === 0 && contextBefore.length > 0) {
    //     firstPrefix = text.substring(0, contextBefore.length);
    // }

    // // Ensure we don't duplicate the before context
    // let mainPrefix = '';
    // if (beforeLen > 0) {
    //     // Only add "…" if not at the very beginning
    //     if (matchIndex - beforeLen > 0) mainPrefix = '…';
    // }

    // // Similarly for suffix/after context
    // let mainSuffix = '';
    // if (afterLen > 0 && (matchEnd + afterLen < text.length)) {
    //     mainSuffix = '…';
    // }

    // // Combine all chunks
    // return (
    //     <>
    //         {/* some first work for user's context */}
    //         {firstPrefix}
    //         {mainPrefix}
    //         {before}
    //         <mark style={{ backgroundColor: '#ffff00' }}>
    //             {text.substring(matchIndex, matchEnd)}
    //         </mark>
    //         {after}
    //         {mainSuffix}
    //     </>
    // );


    // if (!text) return '';
    // if (!maxChars) maxChars = 9999;

    // // If no search term, just truncate text.
    // if (!searchTerm) {
    //     if (text.length <= maxChars) return text;
    //     return text.slice(0, maxChars) + '...';
    // }

    // const lowerText = text.toLowerCase();
    // const lowerSearch = searchTerm.toLowerCase();
    // const matchIndex = lowerText.indexOf(lowerSearch);

    // if (matchIndex === -1) {
    //     // No match. Truncate normally.
    //     if (text.length <= maxChars) return text;
    //     return text.slice(0, maxChars) + '...';
    // }

    // const matchEnd = matchIndex + searchTerm.length;

    // // Calculate how much context to show before and after the match
    // let remaining = maxChars - searchTerm.length;
    // let before = '';
    // let after = '';

    // // At least show match in the middle if possible
    // // Distribute remaining length equally before and after
    // let beforeLen = Math.floor(remaining / 2);
    // let afterLen = remaining - beforeLen;

    // // Adjust before/after when close to string bounds
    // if (matchIndex < beforeLen) {
    //     beforeLen = matchIndex;
    //     afterLen = remaining - beforeLen;
    // }
    // if ((text.length - matchEnd) < afterLen) {
    //     afterLen = text.length - matchEnd;
    //     beforeLen = remaining - afterLen;
    //     if (beforeLen > matchIndex) beforeLen = matchIndex;
    // }

    // before = text.substring(Math.max(0, matchIndex - beforeLen), matchIndex);
    // after = text.substring(matchEnd, matchEnd + afterLen);

    // let prefix = (matchIndex - beforeLen > 0) ? '...' : '';
    // let suffix = (matchEnd + afterLen < text.length) ? '...' : '';

    // // Compose final result, highlight match
    // return (
    //     <>
    //         {prefix}
    //         {before}
    //         <span style={{ backgroundColor: '#ffff00' }}>
    //             {text.substr(matchIndex, searchTerm.length)}
    //         </span>
    //         {after}
    //         {suffix}
    //     </>
    // );


}

const CategoryCell = React.memo(({ props }) => {
    const { open, setOpen } = props;
    const handleClick = useCallback(() => {
        setOpen(!open);
    }, [open, setOpen]);

    return (
        <Mui.Box
            style={{ width: '100%' }}
            className="pointer d-flex align-items-center"
            onClick={handleClick}>
            <Mui.Typography variant="h4" component={'span'} className="font-weight-normal" style={{ fontSize: '1.7rem' }}>
                {highlightSearchTerm(props.searchTerm, props?.category?.name)}
            </Mui.Typography>
            <Mui.Box className="ml-1">
                {(props.searchTerm ? true : props.open) ?
                    <IconChevronDown width="10" height="10" color="#808089" /> :
                    <IconChevronRight width="10" height="10" color="#808089" />}
            </Mui.Box>
        </Mui.Box>
    );
});

const ProductCell = React.memo(({ props }) => {
    const { i18n } = props;
    const productName = useMemo(() =>
        highlightSearchTerm(props.searchTerm, props.extraPrd?.product?.name?.[i18n.language]),
        [props.searchTerm, props.extraPrd?.product?.name, i18n.language]
    );

    return (
        <Mui.Box style={{ paddingLeft: '4rem' }}>
            <Mui.Typography variant="h4" component={'span'} className="font-weight-normal" style={{ fontSize: '1.6rem' }}>
                {productName}
            </Mui.Typography>
        </Mui.Box>
    );
});

const DeductionValueCell = React.memo(({ props }) => {
    const { theme, formikProps, isViewMode } = props;
    const categoryIndex = useMemo(() =>
        getCategoryIndex(formikProps, props.category_id),
        [formikProps, props.category_id]
    );

    const handleChange = useCallback((e) => {
        if (!isNaN(e.target.value)) {
            if (e.target.value > 100) {
                e.target.value = 100;
            }
            const currentValues = props.formikProps.values;
            const updatedValues = {
                ...currentValues,
                productsData: currentValues.productsData.map((category, idx) => {
                    if (idx === categoryIndex) {
                        return {
                            ...category,
                            deductionValue: e.target.value,
                            products: category.products.map(product => ({
                                ...product,
                                subsidy_percentage: e.target.value,
                                ...product?.extraPrd?.finalPrices?.reduce((prevObj, curr) => ({
                                    ...prevObj,
                                    [`product_subsidy_price_vc_${curr.vatCode}`]: (+(curr.priceIncludingVat) * (+(e.target.value) / 100)).toFixed(2)
                                }), {})
                            }))
                        };
                    }
                    return category;
                })
            };
            props.formikProps.setValues(updatedValues);
        } else {
            props.formikProps.setFieldValue(`productsData.${categoryIndex}.deductionValue`, e.target.value);
        }
    }, [categoryIndex, props.formikProps]);

    return (
        <Mui.Box className="form-group" style={{ margin: 0 }}>
            <PriceInputField
                name={`productsData.${categoryIndex}.deductionValue`}
                endAdornment={
                    <Mui.Box style={{ background: theme === 'dark' ? '#9aa1a740' : '#F8F9FA', padding: '1.2rem 1.5rem', borderTopRightRadius: '0.5rem', borderBottomRightRadius: '0.5rem' }}>
                        %
                    </Mui.Box>
                }
                onChange={handleChange}
                disabled={isViewMode}
            />
        </Mui.Box>
    );
});

const SubsidyPercentageCell = React.memo(({ props }) => {
    const { theme, isViewMode } = props;
    const categoryIndex = getCategoryIndex(props.formikProps, props?.extraPrd?.product?.productCategoryId);
    const productIndex = getCategoryProductIndex(props.formikProps, categoryIndex, props.extraPrd?.product?.id);
    return <Mui.Box className="form-group" style={{ margin: 0 }}>
        <PriceInputField
            name={`productsData.${categoryIndex}.products.${productIndex}.subsidy_percentage`}
            disabled={isViewMode}
            endAdornment={
                <Mui.Box style={{ background: theme === 'dark' ? '#9aa1a740' : '#F8F9FA', padding: '1.2rem 1.5rem', borderTopRightRadius: '0.5rem', borderBottomRightRadius: '0.5rem' }}>
                    %
                </Mui.Box>
            }
            onChange={(e) => {
                if (!isNaN(e.target.value)) {
                    if (e.target.value > 100) {
                        e.target.value = 100
                    }
                    props.formikProps.setFieldValue(`productsData.${categoryIndex}.products.${productIndex}.subsidy_percentage`, e.target.value);

                    const currentValues = props.formikProps.values.productsData[categoryIndex];
                    const updatedProducts = currentValues.products.map(product => {
                        if (product.product_uuid === props.extraPrd?.product?.uuid) {
                            return ({
                                ...product,
                                subsidy_percentage: e.target.value,
                                ...product?.extraPrd?.finalPrices?.reduce((prevObj, curr) => ({ ...prevObj, [`product_subsidy_price_vc_${curr.vatCode}`]: (+(curr.priceIncludingVat) * (+(e.target.value) / 100)).toFixed(2) }), {})
                            })
                        }
                        return product;
                    });
                    props.formikProps.setFieldValue(`productsData.${categoryIndex}.products`, updatedProducts);

                }
                else {
                    props.formikProps.setFieldValue(`productsData.${categoryIndex}.products.${productIndex}.subsidy_percentage`, e.target.value);
                }
            }}
        />
    </Mui.Box>
})

const StatusCell = React.memo(({ props }) => {
    const { t } = useTranslation();
    const categoryIndex = useMemo(() =>
        getCategoryIndex(props.formikProps, props.category_id),
        [props.formikProps, props.category_id]
    );

    const handleChange = useCallback((e) => {
        const currentValues = props.formikProps.values;
        const updatedValues = {
            ...currentValues,
            productsData: currentValues.productsData.map((category, idx) => {
                if (idx === categoryIndex) {
                    return {
                        ...category,
                        status: e.target.checked ? 1 : 0,
                        products: category.products.map(product => ({
                            ...product,
                            status: e.target.checked ? 1 : 0,
                            ...product?.extraPrd?.finalPrices?.reduce((prevObj, curr) => ({
                                ...prevObj,
                                [`status_subsidy_vc_${curr.vatCode}`]: e.target.checked ? 1 : 0
                            }), {})
                        }))
                    };
                }
                return category;
            })
        };
        props.formikProps.setValues(updatedValues);
    }, [categoryIndex, props.formikProps]);

    const isChecked = useMemo(() =>
        props?.formikProps?.values?.productsData?.[categoryIndex]?.status === 1,
        [props.formikProps?.values?.productsData, categoryIndex]
    );

    return (
        <Mui.Box className="d-flex align-items-center" style={{ gap: '1rem' }}>
            <StyledSwitch
                checked={isChecked}
                onChange={handleChange}
                name={`productsData.${categoryIndex}.status`}
                inputProps={{ "aria-label": "secondary checkbox" }}
                disabled={props.isViewMode}
            />
            <Mui.Typography variant="h6" className="font-weight-medium" style={{ color: '#495C6E', fontSize: '1.35rem' }}>{t("CANTEEN.PRICE_RULE.ENABLE_LABEL")}</Mui.Typography>
        </Mui.Box>
    );
});

const ProductStatusCell = React.memo(({ props }) => {
    const { t } = useTranslation();
    const categoryIndex = useMemo(() =>
        getCategoryIndex(props.formikProps, props?.extraPrd?.product?.productCategoryId),
        [props.formikProps, props?.extraPrd?.product?.productCategoryId]
    );

    const productIndex = useMemo(() =>
        getCategoryProductIndex(props.formikProps, categoryIndex, props.extraPrd?.product?.id),
        [props.formikProps, categoryIndex, props.extraPrd?.product?.id]
    );

    const handleChange = useCallback((e) => {
        const currentValues = props.formikProps.values;
        const updatedValues = {
            ...currentValues,
            productsData: currentValues.productsData.map((category, idx) => {
                if (idx === categoryIndex) {
                    return {
                        ...category,
                        products: category.products.map((product, prdIdx) => {
                            if (prdIdx === productIndex) {
                                return ({
                                    ...product,
                                    status: e.target.checked ? 1 : 0,
                                    ...product?.extraPrd?.finalPrices?.reduce((prevObj, curr) => ({
                                        ...prevObj,
                                        [`status_subsidy_vc_${curr.vatCode}`]: e.target.checked ? 1 : 0
                                    }), {})
                                })
                            }
                            return product;
                        })
                    };
                }
                return category;
            })
        };
        props.formikProps.setValues(updatedValues);
    }, [categoryIndex, productIndex, props.formikProps]);

    const isChecked = useMemo(() =>
        props?.formikProps?.values?.productsData?.[categoryIndex]?.products?.[productIndex]?.status === 1,
        [props.formikProps?.values?.productsData, categoryIndex, productIndex]
    );

    return (
        <Mui.Box className="d-flex align-items-center" style={{ gap: '1rem' }}>
            <StyledSwitch
                checked={isChecked}
                onChange={handleChange}
                name={`productsData.${categoryIndex}.products.${productIndex}.status`}
                inputProps={{ "aria-label": "secondary checkbox" }}
                disabled={props.isViewMode}
            />
            <Mui.Typography variant="h6" className="font-weight-medium" style={{ color: '#495C6E', fontSize: '1.35rem' }}>{t("CANTEEN.PRICE_RULE.ENABLE_LABEL")}</Mui.Typography>
        </Mui.Box>
    );
});

export const useProductPriceRuleColumns = (isViewMode) => {
    const { t } = useTranslation();
    return useMemo(() => [
        {
            id: 'category',
            label: t("CANTEEN.PRICE_RULE.CATEGORY"),
            flex: 2,
            Cell: (props) => <CategoryCell props={props} />,
            row: {
                name: 'product_name',
                style: { paddingLeft: '1rem' },
                Cell: (props) => <ProductCell props={props} />
            }
        },
        {
            id: 'deductionValue',
            label: t("CANTEEN.PRICE_RULE.SUBSIDY_VALUE"),
            flex: 2,
            Cell: (props) => <DeductionValueCell props={{ ...props, isViewMode }} />,
            row: {
                name: 'subsidy_percentage',
                Cell: (props) => <SubsidyPercentageCell props={{ ...props, isViewMode }} />,
            }
        },
        {
            id: 'status',
            label: t("CANTEEN.PRICE_RULE.DISCOUNT_SUBSIDY_STATUS"),
            flex: 2,
            Cell: (props) => <StatusCell props={{ ...props, isViewMode }} />,
            row: {
                name: 'discount_subsidy_status',
                Cell: (props) => <ProductStatusCell props={{ ...props, isViewMode }} />,
            }
        },
        {
            id: 'actions',
            label: t("COMMON.FORM.ACTION.LABEL"),
            flex: 2,
            align: 'right',
            Cell: (props) => {
                return <RemoveCategory {...props} isViewMode={isViewMode} />
            },
            row: {
                name: 'actions',
                style: {
                    paddingRight: '1rem',
                },
                Cell: (props) => {
                    return <RemoveProductFromCategory {...props} t={t} isViewMode={isViewMode} />
                },
            }
        },
    ], [t, isViewMode]);
}

export const usePriceRuleProductColumns = () => {
    const { i18n, t } = useTranslation();

    const PriceCell = useCallback((props) => (
        <Mui.Box style={{ padding: '1rem 0rem' }}>
            <Mui.Typography variant="h4" component={'span'} style={{ color: '#9AA1A7', fontSize: '1.7rem', fontWeight: '300' }}>
                {formatCurrency(props.price_incl_vat, i18n.language)}
            </Mui.Typography>
        </Mui.Box>
    ), [i18n.language]);

    const VatRateCell = useCallback((props) => (
        <Mui.Box style={{ padding: '1rem 0rem' }}>
            <Mui.Typography variant="h4" component={'span'} style={{ color: '#9AA1A7', fontSize: '1.7rem', fontWeight: '300' }}>
                {props.vat_rate}%
            </Mui.Typography>
        </Mui.Box>
    ), []);

    const SubsidyAmountCell = useCallback((props) => (
        <Mui.Box style={{ padding: '1rem 0rem' }}>
            <Mui.Typography variant="h4" component={'span'} style={{ color: '#9AA1A7', fontSize: '1.7rem', fontWeight: '300' }}>
                {formatCurrency(props.subsidy_amount_incl_vat, i18n.language)}
            </Mui.Typography>
        </Mui.Box>
    ), [i18n.language]);

    const SellingPriceCell = useCallback((props) => (
        <Mui.Box style={{ padding: '1rem 0rem' }}>
            <Mui.Typography variant="h4" component={'span'} style={{ color: '#9AA1A7', fontSize: '1.7rem', fontWeight: '300' }}>
                {formatCurrency(props.selling_price_incl_vat, i18n.language)}
            </Mui.Typography>
        </Mui.Box>
    ), [i18n.language]);

    const ActiveCell = useCallback((props) => (
        <Mui.Box>
            <CheckboxTable checked={props.active === 1} color="primary" />
        </Mui.Box>
    ), []);

    return useMemo(() => [
        {
            id: 'price_incl_vat',
            label: t("CANTEEN.PRICE_RULE.PRICE_INCL_VAT"),
            flex: 2,
            align: 'left',
            Cell: PriceCell
        },
        {
            id: 'vat_rate',
            label: t("CANTEEN.PRICE_RULE.VAT_RATE"),
            flex: 2,
            align: 'center',
            Cell: VatRateCell
        },
        {
            id: 'subsidy_amount_incl_vat',
            label: t("CANTEEN.PRICE_RULE.SUBSIDY_AMOUNT_INCL_VAT"),
            flex: 2,
            align: 'center',
            Cell: SubsidyAmountCell
        },
        {
            id: 'selling_price_incl_vat',
            label: t("CANTEEN.PRICE_RULE.SELLING_PRICE_INCL_VAT"),
            flex: 2,
            align: 'center',
            Cell: SellingPriceCell
        },
        {
            id: 'active',
            label: t("COMMON.ACTIVE_LABEL"),
            flex: 2,
            align: 'right',
            Cell: ActiveCell
        }
    ], [PriceCell, VatRateCell, SubsidyAmountCell, SellingPriceCell, ActiveCell, t]);
}

const RemoveCategory = ({ fieldArrayProps, index, theme, isViewMode, ...props }) => {
    const { t } = useTranslation();
    const [popoverAnchor, setPopoverAnchor] = useState(null);
    const [popoverOpen, setPopoverOpen] = useState(false);
    const categoryIndex = useMemo(() => getCategoryIndex(props.formikProps, props.category_id), [props.formikProps, props.category_id]);

    const handleClick = useCallback((e) => {
        setPopoverAnchor(e.currentTarget);
        setPopoverOpen(true);
    }, []);

    const handleClose = useCallback(() => {
        setPopoverAnchor(null);
        setPopoverOpen(false);
    }, []);

    const handleContinue = useCallback(() => {
        fieldArrayProps.remove(categoryIndex);
        handleClose();
    }, [categoryIndex, fieldArrayProps, handleClose]);

    return (
        isViewMode ? <></> : <>
            <Mui.Box onClick={handleClick} className="d-flex align-items-center pointer" style={{ gap: '1rem' }}>
                <IconTrash fill={theme === 'dark' ? '#808089' : '#495C6E'} />
            </Mui.Box>
            <WarningPopover
                onClose={handleClose}
                onContinue={handleContinue}
                popoverAnchor={popoverAnchor}
                popoverOpen={popoverOpen}
                title={t("CANTEEN.PRICE_RULE.REMOVE_CATEGORY")}
                description={t("CANTEEN.PRICE_RULE.REMOVE_CATEGORY_DESC")}
            />
        </>
    );
};

const RemoveProductFromCategory = ({ theme, t, isViewMode, ...props }) => {
    // const { onPriceDetailsClick, extraPrd } = props;
    // const productUuid = extraPrd?.product?.uuid;
    const [popoverAnchor, setPopoverAnchor] = useState(null);
    const [popoverOpen, setPopoverOpen] = useState(false);
    const categoryIndex = useMemo(() => getCategoryIndex(props.formikProps, props?.extraPrd?.product?.productCategoryId), [props.formikProps, props?.extraPrd?.product?.productCategoryId]);
    const productIndex = useMemo(() => getCategoryProductIndex(props.formikProps, categoryIndex, props.extraPrd?.product?.id), [props.formikProps, categoryIndex, props.extraPrd?.product?.id]);

    const handleClick = useCallback((e) => {
        setPopoverAnchor(e.currentTarget);
        setPopoverOpen(true);
    }, []);

    const handleClose = useCallback(() => {
        setPopoverAnchor(null);
        setPopoverOpen(false);
    }, []);

    const handleContinue = useCallback(() => {
        const currentValues = props.formikProps.values.productsData[categoryIndex];
        const updatedProducts = currentValues.products.filter((_, idx) => idx.toString() !== productIndex.toString());
        props.formikProps.setFieldValue(`productsData.${categoryIndex}.products`, updatedProducts);
        handleClose();
    }, [categoryIndex, productIndex, props.formikProps, handleClose]);

    // const handlePriceDetailsClick = useCallback(() => {
    //     onPriceDetailsClick(categoryIndex, productUuid);
    // }, [categoryIndex, productUuid, onPriceDetailsClick]);

    return (
        <>
            <Mui.Box style={{ width: '100%', gap: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                {/* <Mui.Box onClick={handlePriceDetailsClick} className="pointer" style={{ border: '2px solid #D5DCE6', borderRadius: '0.5rem', padding: '0.65rem 1rem' }}>
                    <Mui.Typography variant="h6" className="font-weight-normal" style={{ color: '#495C6E', fontSize: '1.45rem' }}>{t("CANTEEN.PRICE_RULE.PRICE_DETAILS")}</Mui.Typography>
                </Mui.Box> */}
                {!isViewMode && <IconTrash
                    className="pointer"
                    onClick={handleClick}
                    fill={theme === 'dark' ? '#808089' : '#495C6E'} />}
            </Mui.Box>
            <WarningPopover
                onClose={handleClose}
                onContinue={handleContinue}
                popoverAnchor={popoverAnchor}
                popoverOpen={popoverOpen}
                title={t("CANTEEN.PRICE_RULE.REMOVE_PRODUCT_FROM_CATEGORY")}
                description={t("CANTEEN.PRICE_RULE.REMOVE_PRODUCT_FROM_CATEGORY_DESC")}
            />
        </>
    );
};